import type { ServiceId, StructuralPieceId, UnitTypeId, WeeklyAllocationId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import type { AppliesTo, WithSideMargin } from './applies-to';
import type { GroundBeam, PileCap } from './foundation';
import { computeGeometry, summarizeGeometry, totalFor } from './geometry';
import type { PieceMark, PileType, StructuralPiece } from './pile';
import { nomeDaPeca } from './planilha-por-peca';
import type { RetainingColumn, RetainingWall } from './retaining';
import type { Slab } from './slab';
import type { Stair } from './stair';

/**
 * O apontamento por peça (Fase 27, etapa 6 — D19).
 *
 * No lançamento do dia (Conferência e RDO), a frente que mede uma peça mostra as peças dela, e
 * o encarregado marca o que ficou pronto: "E76-A perfurada". Três decisões do usuário (06/10):
 *
 * - **a marca é peça × serviço** (RN-381): a estaca perfurada hoje, armada amanhã, concretada
 *   depois — cada serviço tem a sua data. Uma peça não se executa duas vezes no mesmo serviço;
 * - **a soma preenche, e dá para mudar** (RN-382): marcar duas estacas de 16 m preenche 32 m no
 *   campo do dia, que continua editável — como a produção pela medida do RDO (RN-379);
 * - **o cronograma por item sai do executado** (RN-383): o avanço do item (frente × serviço) é
 *   o executado ÷ a quantidade dele, e deixa de ser digitado onde houver medição.
 *
 * A medida de uma peça é a mesma conta do quantitativo (`totalFor`), feita sobre o desenho com
 * só aquela peça do tipo dela — as peças dos outros tipos ficam, porque o baldrame se corta no
 * bloco e o muro cede ao pilar (RN-368, RN-372).
 */

/** As peças de uma tipologia — o que o quantitativo conta dela, sem ambientes nem paredes. */
export interface PiecesOfDrawing {
  readonly pieces?: readonly StructuralPiece[];
  readonly pileTypes?: readonly PileType[];
  readonly pieceMarks?: readonly PieceMark[];
  readonly pileCaps?: readonly PileCap[];
  readonly groundBeams?: readonly GroundBeam[];
  readonly slabs?: readonly Slab[];
  readonly retainingWalls?: readonly RetainingWall[];
  readonly retainingColumns?: readonly RetainingColumn[];
  readonly stairs?: readonly Stair[];
}

export type PieceKind =
  | 'ESTACA'
  | 'BLOCO'
  | 'BALDRAME'
  | 'RADIER'
  | 'LAJE'
  | 'MURO_CONTENCAO'
  | 'PILAR_CONTENCAO'
  | 'ESCADA';

/** A peça que o serviço mede — o elemento do "Aplica a", quando é uma peça da família. */
export function pieceKindOf(appliesTo: AppliesTo | undefined): PieceKind | undefined {
  const pecas: readonly AppliesTo[] = [
    'ESTACA',
    'BLOCO',
    'BALDRAME',
    'RADIER',
    'LAJE',
    'MURO_CONTENCAO',
    'PILAR_CONTENCAO',
    'ESCADA',
  ];
  return appliesTo !== undefined && pecas.includes(appliesTo)
    ? (appliesTo as PieceKind)
    : undefined;
}

/** Uma peça do desenho, qualquer que seja o tipo dela: o id, o tipo, o nome e a tipologia. */
export interface PieceRef {
  readonly id: StructuralPieceId;
  readonly kind: PieceKind;
  readonly name: string;
  readonly unitTypeId: UnitTypeId;
}

function vivas<T extends { readonly deletedAt?: string }>(lista: readonly T[] | undefined): T[] {
  return (lista ?? []).filter((item) => item.deletedAt === undefined);
}

/**
 * As peças deste tipo no desenho, na ordem do nome. A estaca a posicionar fica de fora: ela não
 * conta no quantitativo (RN-364), e não pode ser executada antes de estar no desenho.
 */
export function piecesOfKind(drawing: PiecesOfDrawing, kind: PieceKind): readonly PieceRef[] {
  const lista: { id: StructuralPieceId; name: string; unitTypeId: UnitTypeId }[] = (() => {
    switch (kind) {
      case 'ESTACA':
        return vivas(drawing.pieces).filter((peca) => peca.center !== undefined);
      case 'BLOCO':
        return vivas(drawing.pileCaps);
      case 'BALDRAME':
        return vivas(drawing.groundBeams);
      case 'RADIER':
      case 'LAJE':
        return vivas(drawing.slabs).filter((laje) => laje.kind === kind);
      case 'MURO_CONTENCAO':
        return vivas(drawing.retainingWalls);
      case 'PILAR_CONTENCAO':
        return vivas(drawing.retainingColumns);
      case 'ESCADA':
        return vivas(drawing.stairs);
    }
  })();
  return lista
    .map((peca) => ({ id: peca.id, kind, name: peca.name, unitTypeId: peca.unitTypeId }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }));
}

/** O desenho com só esta peça do tipo dela — as dos outros tipos ficam, para os descontos. */
function soEsta(drawing: PiecesOfDrawing, kind: PieceKind, id: StructuralPieceId): PiecesOfDrawing {
  const so = <T extends { readonly id: StructuralPieceId }>(lista: readonly T[] | undefined) =>
    (lista ?? []).filter((item) => item.id === id);
  switch (kind) {
    case 'ESTACA':
      return { ...drawing, pieces: so(drawing.pieces) };
    case 'BLOCO':
      return { ...drawing, pileCaps: so(drawing.pileCaps) };
    case 'BALDRAME':
      return { ...drawing, groundBeams: so(drawing.groundBeams) };
    case 'RADIER':
    case 'LAJE':
      return {
        ...drawing,
        slabs: (drawing.slabs ?? []).filter((laje) => laje.kind !== kind || laje.id === id),
      };
    case 'MURO_CONTENCAO':
      return { ...drawing, retainingWalls: so(drawing.retainingWalls) };
    case 'PILAR_CONTENCAO':
      return { ...drawing, retainingColumns: so(drawing.retainingColumns) };
    case 'ESCADA':
      return { ...drawing, stairs: so(drawing.stairs) };
  }
}

/**
 * A medida desta peça para este serviço — o que marcá-la soma ao dia (RN-382).
 *
 * `undefined` quando o serviço não mede este tipo de peça, ou não tem como medir (o aço sem
 * taxa, a marca de ensaio). É a conta do quantitativo, inteira, sobre o desenho com a peça só.
 */
export function pieceMeasure(
  service: WithSideMargin,
  drawing: PiecesOfDrawing,
  piece: Pick<PieceRef, 'id' | 'kind'>,
): number | undefined {
  if (pieceKindOf(service.appliesTo) !== piece.kind) return undefined;
  const geometria = computeGeometry({
    rooms: [],
    walls: [],
    ...soEsta(drawing, piece.kind, piece.id),
  });
  return totalFor(service, summarizeGeometry(geometria));
}

// ── A peça da frente: o desenho e a planilha (Fase 28, etapa 2 · RN-411, RN-412) ─────

/** De onde veio a medida da peça: o número da planilha importada ou a conta do desenho. */
export type FrontPieceSource = 'PLANILHA' | 'DESENHO';

/** Uma peça desenhada do tipo que o serviço mede, com a conta do desenho para ele. */
export interface DrawnFrontPiece {
  readonly ref: PieceRef;
  /** `pieceMeasure` — ausente quando o desenho não tem como medir a peça neste serviço. */
  readonly measure?: number;
}

/**
 * Uma linha da planilha por peça (`piece_quantities`, RN-390) deste serviço. A do serviço traz
 * a quantidade dele na peça (RN-388); a de um item só diz que a peça tem o serviço — vem sem
 * `quantity`, e a peça entra na lista sem medida.
 */
export interface ImportedFrontPiece {
  readonly unitTypeId: UnitTypeId;
  readonly name: string;
  /** A quantidade do **serviço** na peça; ausente na linha de item. */
  readonly quantity?: number;
}

/** Uma peça da frente para um serviço — o que o encarregado marca no dia (RN-411). */
export interface FrontPiece {
  /** Como a tela mostra e a marca grava: o nome do desenho, ou o da planilha. */
  readonly name: string;
  /** A tipologia de onde a peça vem — com o nome, a chave da marca (RN-413). */
  readonly unitTypeId: UnitTypeId;
  /** A peça desenhada com este nome, quando há. */
  readonly pieceId?: StructuralPieceId;
  readonly kind?: PieceKind;
  readonly source: FrontPieceSource;
  /** O que marcá-la soma ao dia (RN-412); ausente, marca e não soma. */
  readonly measure?: number;
}

/**
 * As peças de uma frente × serviço: as desenhadas do tipo que o serviço mede e as importadas
 * com alguma linha do serviço, juntas pelo nome (`nomeDaPeca`: sem caixa nem espaço sobrando).
 *
 * - **RN-411** — a peça só da planilha entra sem desenho; a desenhada sem planilha, como antes;
 * - **RN-412** — a quantidade do serviço na planilha vence a conta do desenho, como no
 *   quantitativo (RN-384). Sem ela, vale o desenho; sem nenhuma, a peça vem sem medida.
 *
 * `source` é `PLANILHA` quando a peça só existe na planilha ou quando é dela a medida; senão,
 * `DESENHO`. O nome e o id são os do desenho quando a peça está desenhada. A ordem é a do
 * número: B2 antes de B10.
 *
 * O nome é único na área comum do desenho (RN-376), mas a frente pode ter mais de uma área
 * comum posicionada: o mesmo nome em duas é a mesma peça para a frente — a primeira que vier
 * diz a tipologia —, porque é pelo nome que a tela marca.
 */
export function frontPieces(
  drawn: readonly DrawnFrontPiece[],
  imported: readonly ImportedFrontPiece[],
): readonly FrontPiece[] {
  const desenhadas = new Map<string, DrawnFrontPiece>();
  for (const peca of drawn) {
    const chave = nomeDaPeca(peca.ref.name);
    if (!desenhadas.has(chave)) desenhadas.set(chave, peca);
  }
  const importadas = new Map<string, { primeira: ImportedFrontPiece; quantidade?: number }>();
  for (const linha of imported) {
    const chave = nomeDaPeca(linha.name);
    const atual = importadas.get(chave);
    if (atual === undefined) {
      importadas.set(chave, {
        primeira: linha,
        ...(linha.quantity === undefined ? {} : { quantidade: linha.quantity }),
      });
    } else if (atual.quantidade === undefined && linha.quantity !== undefined) {
      atual.quantidade = linha.quantity;
    }
  }

  const pecas: FrontPiece[] = [];
  for (const [chave, desenhada] of desenhadas) {
    const quantidade = importadas.get(chave)?.quantidade;
    const medida = quantidade ?? desenhada.measure;
    pecas.push({
      name: desenhada.ref.name,
      unitTypeId: desenhada.ref.unitTypeId,
      pieceId: desenhada.ref.id,
      kind: desenhada.ref.kind,
      source: quantidade === undefined ? 'DESENHO' : 'PLANILHA',
      ...(medida === undefined ? {} : { measure: medida }),
    });
  }
  for (const [chave, { primeira, quantidade }] of importadas) {
    if (desenhadas.has(chave)) continue;
    pecas.push({
      name: primeira.name,
      unitTypeId: primeira.unitTypeId,
      source: 'PLANILHA',
      ...(quantidade === undefined ? {} : { measure: quantidade }),
    });
  }
  return pecas.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }));
}

// ── A execução ──────────────────────────────────────────────────────────────────

/**
 * A peça executada num serviço, num dia (RN-381). Única por tipologia × nome × serviço entre as
 * vivas (RN-413): refazer é desfazer a marca e marcar de novo. Guarda a alocação do dia em que
 * foi lançada, quando houver — é o que liga a marca à quantidade que ela preencheu.
 *
 * A marca é pelo **nome**, e não pelo id (ADR-178): a peça só da planilha não tem id, e o
 * "B93" importado é o mesmo B93 que um dia for desenhado. O id vai junto quando a peça estava
 * desenhada — a marca de antes da `0069` só tinha ele, e é por ele que ela ainda se acha se a
 * peça mudou de nome depois.
 */
export interface PieceExecution {
  readonly unitTypeId: UnitTypeId;
  /** Como a frente mostrava a peça quando ela foi marcada. */
  readonly pieceName: string;
  /** A peça desenhada, quando havia. */
  readonly pieceId?: StructuralPieceId;
  readonly serviceId: ServiceId;
  /** AAAA-MM-DD — o dia do lançamento. */
  readonly date: string;
  readonly allocationId?: WeeklyAllocationId;
}

/** `tipologia|NOME` — a chave da marca sem o serviço (RN-413), pelo nome normalizado. */
export function pieceExecutionKey(unitTypeId: string, pieceName: string): string {
  return `${unitTypeId}|${nomeDaPeca(pieceName)}`;
}

/** `tipologia|NOME|serviço` — a chave natural da marca viva (RN-413), o índice da `0069`. */
export function pieceExecutionNaturalKey(
  unitTypeId: string,
  pieceName: string,
  serviceId: string,
): string {
  return `${pieceExecutionKey(unitTypeId, pieceName)}|${serviceId}`;
}

/**
 * A marca viva de cada peça da frente, pelo nome normalizado da peça (`nomeDaPeca`).
 *
 * A marca é da peça quando aponta a mesma tipologia e o mesmo nome (RN-413) — ou o mesmo id
 * de peça desenhada: a marca de antes da `0069` ganhou o nome que a peça tinha então, e a peça
 * pode ter sido renomeada depois. Duas marcas para a mesma peça não deveriam existir; se
 * existirem, vale a primeira da lista (a mais antiga, na ordem do repositório).
 */
export function executionsOfPieces<E extends PieceExecution>(
  pieces: readonly Pick<FrontPiece, 'name' | 'unitTypeId' | 'pieceId'>[],
  executions: readonly E[],
): ReadonlyMap<string, E> {
  const peloId = new Map<string, string>();
  const pelaChave = new Map<string, string>();
  for (const peca of pieces) {
    const nome = nomeDaPeca(peca.name);
    if (peca.pieceId !== undefined) peloId.set(peca.pieceId, nome);
    pelaChave.set(pieceExecutionKey(peca.unitTypeId, peca.name), nome);
  }
  const marcas = new Map<string, E>();
  for (const marca of executions) {
    const nome =
      (marca.pieceId === undefined ? undefined : peloId.get(marca.pieceId)) ??
      pelaChave.get(pieceExecutionKey(marca.unitTypeId, marca.pieceName));
    if (nome !== undefined && !marcas.has(nome)) marcas.set(nome, marca);
  }
  return marcas;
}

/** A soma das medidas das peças marcadas — o que vai para o campo do dia (RN-382). */
export function executedFromPieces(measures: readonly (number | undefined)[]): number | undefined {
  const comMedida = measures.filter((medida): medida is number => medida !== undefined);
  if (comMedida.length === 0) return undefined;
  return roundQuantity(
    comMedida.reduce((soma, medida) => soma + medida, 0),
    3,
  );
}

// ── O avanço do item do cronograma (RN-383) ─────────────────────────────────────

/**
 * O executado de um item (frente × serviço), somando os dias.
 *
 * A quantidade do dia é lançada por pessoa (`weekly_allocations.executed_quantity`) e, na obra
 * por equipe, por equipe (`team_measurements`, Fase 30). As duas chegam aqui como dias do
 * mesmo jeito — uma linha por lançamento —, e o que elas querem dizer depende do serviço
 * (decisão do usuário, 07/10/2026; RN-408):
 *
 * - **o serviço que mede peça** (estaca, bloco, laje…) — `byFront` — mede a frente: as
 *   pessoas e as equipes marcam as mesmas peças e lançam o mesmo número. Por dia vale o
 *   **maior** entre elas; somar contaria a mesma estaca duas vezes;
 * - **os demais** (alvenaria, reboco…) somam: cada pessoa lança a própria produção, como no
 *   RDO de referência (Márcio 0,71 + Maicon 1 = 1,71), e cada equipe, a dela.
 *
 * Os dias se somam nos dois casos.
 */
export function executedOfItem(
  days: readonly { readonly date: string; readonly executedQuantity?: number }[],
  byFront = true,
): number | undefined {
  const porDia = new Map<string, number>();
  for (const dia of days) {
    if (dia.executedQuantity === undefined) continue;
    const atual = porDia.get(dia.date) ?? 0;
    porDia.set(
      dia.date,
      byFront ? Math.max(atual, dia.executedQuantity) : atual + dia.executedQuantity,
    );
  }
  if (porDia.size === 0) return undefined;
  return roundQuantity(
    [...porDia.values()].reduce((soma, valor) => soma + valor, 0),
    3,
  );
}

/**
 * O avanço do item, em %, pelo executado (RN-383). `undefined` quando não há medição ou o item
 * não tem quantidade — aí vale o percentual digitado, como antes. Nunca passa de 100.
 */
export function progressFromExecuted(
  executed: number | undefined,
  quantity: number | undefined,
): number | undefined {
  if (executed === undefined || quantity === undefined || quantity <= 0) return undefined;
  return Math.min(100, Math.round((executed / quantity) * 10000) / 100);
}
